import React, { useEffect, useState } from 'react';
import { Box, Button, Fade, Paper, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import useTeamColors from '../../hooks/useTeamColors';

const SUIT_CODES = { oros: 1, copas: 2, espadas: 3, bastos: 4 };

function cardImagePath(suit, value) {
    return `assets/images/deck/${SUIT_CODES[suit]}${String(value).padStart(2, '0')}.png`;
}

const SUIT_NAME_KEY = { oros: 'setup.suits.oros', copas: 'setup.suits.copas', espadas: 'setup.suits.espadas', bastos: 'setup.suits.bastos' };

/**
 * Online equivalent of the offline GameStartSequence.jsx ceremony — same
 * purpose (reveal who deals first) and result-panel visual language, but
 * simplified: no PixiJS deck-extraction animation, just a brief reveal fade
 * into the result, since the actual random draw already happened
 * server-side (cutTheDeck.ts, at room start) — this component only ever
 * DISPLAYS that already-decided result, never generates its own (the
 * client must never predict/influence randomness, RONDA_ONLINE.md's
 * standing principle for every other shuffle/deal in this app).
 */
export default function CutTheDeckModal({ cuttingSeat, cardSuit, cardValue, dealerSeat, playersBySeat, onContinue }) {
    const { t } = useTranslation();
    const { teamColors } = useTeamColors();
    const [revealed, setRevealed] = useState(false);

    useEffect(() => {
        const timer = setTimeout(() => setRevealed(true), 900);
        return () => clearTimeout(timer);
    }, []);

    const cuttingPlayer = playersBySeat?.[cuttingSeat] ?? { name: '?', isSystem: false };
    const dealer = playersBySeat?.[dealerSeat] ?? { name: '?', isSystem: false };

    return (
        <Box
            sx={{
                position: 'fixed',
                inset: 0,
                zIndex: 9999,
                backgroundColor: 'rgba(0, 0, 0, 0.95)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 2,
            }}
        >
            <Fade in timeout={800}>
                <Paper
                    elevation={10}
                    sx={{
                        p: { xs: 3, sm: 4 },
                        maxWidth: { xs: '90%', sm: 450 },
                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                        borderRadius: 3,
                        textAlign: 'center',
                    }}
                >
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                        {t('online.play.cut_deck.title')}
                    </Typography>

                    {!revealed ? (
                        <Typography variant="body1" sx={{ my: 3, color: 'text.secondary' }}>
                            {t('online.play.cut_deck.cutting', { name: cuttingPlayer.name })}
                        </Typography>
                    ) : (
                        <Fade in timeout={500}>
                            <Box>
                                <Typography variant="body1" sx={{ my: 1 }}>
                                    {t('online.play.cut_deck.revealed', {
                                        name: cuttingPlayer.name,
                                        value: cardValue,
                                        suit: t(SUIT_NAME_KEY[cardSuit] || cardSuit),
                                    })}
                                </Typography>
                                <Box
                                    component="img"
                                    src={cardImagePath(cardSuit, cardValue)}
                                    alt={`${cardValue} ${cardSuit}`}
                                    sx={{ width: 90, my: 1, borderRadius: 1, boxShadow: 2 }}
                                />
                                <Box
                                    sx={{
                                        p: 2,
                                        my: 2,
                                        backgroundColor: teamColors.team1.light,
                                        borderRadius: 2,
                                        border: `2px solid ${teamColors.team1.main}`,
                                    }}
                                >
                                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: teamColors.team1.main }}>
                                        {dealer.avatar || (dealer.isSystem ? '🤖' : '🙂')} {dealer.name}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                        {t('online.play.cut_deck.will_deal')}
                                    </Typography>
                                </Box>
                                <Button variant="contained" fullWidth size="large" onClick={onContinue} sx={{ mt: 1, py: 1.5, fontWeight: 'bold' }}>
                                    {t('round.continue')}
                                </Button>
                            </Box>
                        </Fade>
                    )}
                </Paper>
            </Fade>
        </Box>
    );
}
